import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Check } from 'lucide-react';
import Testimonials from '../components/Testimonials';
import { packages as mockPackages, faqs } from '../mock';
import { getPackages } from '../api';
import PromoSlider from '../components/PromoSlider';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { HomeHero } from '../components/home/HomeHero';
import { AuthorityMarquee } from '../components/home/AuthorityMarquee';
import { AIDeepDive } from '../components/home/AIDeepDive';
import { Jurisdictions } from '../components/home/Jurisdictions';
import { Personas } from '../components/home/Personas';
import { ServicesGrid, FinalCTA } from '../components/home/ServicesGrid';

const Home = () => {
  const { t, pick, dir, lang } = useLang();
  const [packages, setPackages] = useState(mockPackages);

  useEffect(() => {
    getPackages().then((d) => d && d.length && setPackages(d)).catch(() => {});
  }, []);

  return (
    <div className="pt-0" dir={dir}>
      <PromoSlider />
      <HomeHero lang={lang} />
      <AuthorityMarquee lang={lang} />
      <AIDeepDive lang={lang} />
      <Jurisdictions lang={lang} />
      <Personas lang={lang} />
      <ServicesGrid lang={lang} />

      {/* Packages Preview */}
      <section className="relative py-24" data-testid="packages-preview">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div className="max-w-xl">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#2563eb] mb-4">{t('nav.pricing')}</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#0a0a0a]">{t('home.packages_title')}</h2>
              <p className="mt-4 text-[#111827]/60">{t('home.packages_sub')}</p>
            </div>
            <Link to="/accounting/pricing" className="text-sm font-semibold text-[#2563eb] hover:underline" data-testid="ai-plans-link">{lang === 'ar' ? 'باقات المحاسبة الذكية ←' : 'AI accounting plans →'}</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {packages.map((p) => (
              <div key={p.id} className={`glass-card rounded-2xl p-8 relative transition-all hover:-translate-y-1 ${p.popular ? 'border-[#2563eb] shadow-xl shadow-blue-500/10' : ''}`}>
                {p.popular && <div className="absolute -top-3 start-8 px-3 py-1 rounded-full bg-[#2563eb] text-white text-[10px] uppercase tracking-widest font-semibold">{t('pricing.popular')}</div>}
                <div className="text-xs uppercase tracking-[0.2em] text-[#2563eb] mb-2">{pick(p.tagline)}</div>
                <h3 className="font-display text-2xl md:text-3xl text-[#0a0a0a] mb-4">{pick(p.name)}</h3>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-xs text-[#111827]/50">{t('pricing.currency')}</span>
                  <span className="font-display text-4xl gold-text">{p.price.toLocaleString()}</span>
                  <span className="text-sm text-[#111827]/50">{t('pricing.per_year')}</span>
                </div>
                <ul className="space-y-2.5 mb-8">
                  {(pick(p.features)).slice(0, 5).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#111827]/75"><Check className="w-4 h-4 text-[#2563eb] mt-0.5 flex-shrink-0" /><span>{f}</span></li>
                  ))}
                </ul>
                <Link to="/Pricing"><Button className={`w-full rounded-xl h-11 text-sm ${p.popular ? 'gold-button' : 'ghost-button bg-transparent'}`}>{t('pricing.choose')}</Button></Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />

      {/* FAQ */}
      <section className="relative py-24" data-testid="faq-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_1.4fr] gap-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#2563eb] mb-4">FAQ</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#0a0a0a]">{t('home.faq_title')}</h2>
            <p className="mt-4 text-[#111827]/60">{lang === 'ar' ? 'لم تجد إجابتك؟ تحدّث معنا مباشرة.' : 'Can’t find your answer? Talk to us directly.'}</p>
            <Link to="/Contact" className="mt-4 inline-block text-sm font-semibold text-[#2563eb] hover:underline">{lang === 'ar' ? 'تواصل معنا ←' : 'Contact us →'}</Link>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="glass-card rounded-xl px-6 border-none">
                <AccordionTrigger className="text-start text-[#0a0a0a] font-medium hover:no-underline py-5">{pick(f.q)}</AccordionTrigger>
                <AccordionContent className="text-[#111827]/65 leading-relaxed pb-5">{pick(f.a)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <FinalCTA lang={lang} />
    </div>
  );
};

export default Home;
